import React, { useState, useEffect, useCallback } from 'react';
import { MDBCol, MDBRow } from 'mdbreact';
import { post } from 'axios';
import PropTypes from 'prop-types';

import { actionSvgs, callbackSvgs } from 'assets/icons';

import PageStructure from 'components/PageStructure';
import If from 'components/If';
import Overlay from 'components/Informations/Overlay';
import ContentLoader from 'components/Informations/Loaders/ContentLoader';
import { renderToast } from 'components/Informations/Toast/utils';

import FilledButton from 'components/Buttons/FilledButton';
import UnfilledButton from 'components/Buttons/UnfilledButton';
import SelectField from 'components/FormGroup/SelectField';

import endpoints from 'services/endpoints';
import { sortArrayInAlphabeticalOrder } from 'utils/objectManipulations';

import ManageListboxValue from '../InternalComponents/ManageListboxValue';

import {
  fieldDescriptions,
  fieldOptions,
  getTableData,
  getSubmitObject,
} from './utils';
import * as S from './styles';

const {
  deleteListboxesValues,
  deleteProjectStatusAndCategoryListboxesValues,
} = endpoints;

export default function ManageListboxes({ user }) {
  const { id_funcionario: userID } = user;

  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const [overlayData, setOverlayData] = useState({});
  const [overlayType, setOverlayType] = useState(null);

  const [toastVisibility, setToastVisibility] = useState(false);
  const [toastInfos, setToastInfos] = useState({
    type: '',
    message: '',
  });

  const [fieldValue, setFieldValue] = useState({});
  const [fieldDescription, setFieldDescription] = useState('');
  const [tableData, setTableData] = useState([]);

  const [observer, setObserver] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);

    (async () => {
      if (fieldValue.value) {
        const data = await getTableData(fieldValue.value);
        const sortedData = sortArrayInAlphabeticalOrder(
          data,
          fieldValue.value === 'funcao' ? 'nome_funcao' : 'descricao',
        );

        setTableData(sortedData);
      }
      setLoading(false);
    })();
  }, [fieldValue, observer]);

  const getFieldDescription = useCallback(value => {
    const { description } = fieldDescriptions.find(
      option => option.type === value,
    );
    setFieldDescription(description);
  }, []);

  const handleItemEdition = useCallback(
    item => {
      setOverlayType('edit');
      setOverlayData({
        id: item.id_funcao ?? item.id ?? null,
        id_area: item.id_area ?? null,
        inputValue: item.descricao ?? item.nome_funcao,
        administrador: item.administrador,
        gestor_direto: item.gestor_direto,
        gerente_projetos: item.gerente_projetos,
        responsavel_tecnico: item.responsavel_tecnico,
        coustCenters:
          item.centros_de_custo?.map(coustCenter => ({
            label: coustCenter[0],
            value: coustCenter[1],
          })) ?? [],
        listName: fieldValue.label,
        fieldValue: fieldValue.value,
        isFunction: fieldValue.value === 'funcao',
        isProject:
          fieldValue.value === 'categoria_projetos' ||
          fieldValue.value === 'status_projetos',
      });
      setIsOverlayOpen(true);
    },
    [fieldValue.label, fieldValue.value],
  );

  const handleItemAdd = useCallback(() => {
    setOverlayType('add');
    setIsOverlayOpen(true);
    setOverlayData({
      listName: fieldValue.label,
      fieldValue: fieldValue.value,
      isFunction: fieldValue.value === 'funcao',
      isProject:
        fieldValue.value === 'categoria_projetos' ||
        fieldValue.value === 'status_projetos',
    });
  }, [fieldValue.label, fieldValue.value]);

  const handleDeleteOverlayOpen = useCallback(
    item => {
      const submitInfos = getSubmitObject(
        fieldValue.value,
        userID,
        item,
        true,
        fieldValue.value === 'funcao',
      );

      const directManager = !!item.gestor_direto;
      const projectManager = !!item.gerente_projetos;
      const technicalResponsive = !!item.responsavel_tecnico;
      const admin = !!item.administrador;

      const additionalInfos = {
        gestor_direto: directManager ? 1 : 0,
        gerente_projetos: projectManager ? 1 : 0,
        responsavel_tecnico: technicalResponsive ? 1 : 0,
        administrador: admin ? 1 : 0,
        id_area: item.id_area,
      };

      if (fieldValue.value === 'funcao') {
        submitInfos.data = {
          ...submitInfos.data,
          ...additionalInfos,
        };
      }

      setOverlayType('remove');
      setIsOverlayOpen(true);
      setOverlayData(submitInfos);
    },
    [fieldValue.value, userID],
  );

  const handleItemRemove = useCallback(async () => {
    try {
      if (
        fieldValue.value === 'categoria_projetos' ||
        fieldValue.value === 'status_projetos'
      ) {
        await post(deleteProjectStatusAndCategoryListboxesValues, overlayData);
      }

      if (
        !(
          fieldValue.value === 'categoria_projetos' ||
          fieldValue.value === 'status_projetos'
        )
      ) {
        await post(deleteListboxesValues, overlayData);
      }

      setToastVisibility(true);
      setToastInfos({
        type: 'success',
        message: 'Item atualizado com sucesso',
      });
      setIsOverlayOpen(false);
      setObserver(current => !current);
    } catch {
      setToastVisibility(true);
      setToastInfos({
        type: 'error',
        message: 'Error ao excluir item. Tente novamente',
      });
      setIsOverlayOpen(false);
    }
  }, [fieldValue.value, overlayData]);

  return (
    <>
      <PageStructure
        title="Edição das listas de seleção"
        subtitle="Edite, insira ou remova informações de todas as listas de seleção da aplicação"
      >
        <MDBRow>
          <MDBCol lg="4" sm="12">
            <SelectField
              options={fieldOptions}
              handleChange={e => [
                setFieldValue(e),
                getFieldDescription(e.value),
              ]}
              value={fieldOptions.find(
                ({ value }) => value === fieldValue.value,
              )}
              labelText="Edite os valores de:"
              placeholderText="Selecione uma lista..."
            />
            <If condition={!!fieldDescription}>
              <S.FieldDescription>
                Essa alteração será refletida nas telas:
                <span>{fieldDescription}</span>
              </S.FieldDescription>
            </If>
          </MDBCol>
        </MDBRow>

        <MDBRow>
          <MDBCol lg="12">
            <S.Table isFunction={fieldValue.value === 'funcao'}>
              <div className="table-responsive">
                <If condition={!tableData.length}>
                  <S.Callback>
                    {callbackSvgs('womanWriting')}
                    <S.CallbackText>
                      Para realizar edições selecione um item na lista
                    </S.CallbackText>
                  </S.Callback>
                </If>

                <If condition={!!tableData.length}>
                  <table className="table">
                    <thead>
                      <tr>
                        <th scope="col">Nome</th>
                        <If condition={fieldValue.value === 'funcao'}>
                          <th scope="col">Área</th>
                          <th scope="col">Centros de Custos</th>
                          <th scope="col" title="Gestor Direto">
                            Gestor Direto
                          </th>
                          <th scope="col" title="Gerente de Projetos">
                            G.P.
                          </th>
                          <th scope="col" title="Responsável Técnico">
                            Resp. Técnico
                          </th>
                        </If>
                        <th scope="col">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tableData.map(item => (
                        <tr key={item.id ?? item.id_funcao}>
                          <td>{item.descricao ?? item.nome_funcao}</td>
                          <If condition={fieldValue.value === 'funcao'}>
                            <td>{item.descricao_area ?? 'Sem atribuição'}</td>
                            <td>
                              {item.centros_de_custo
                                ?.map(coustCenter => coustCenter[0])
                                .join(', ') ?? 'Sem atribuição'}
                            </td>
                            <td>{item.gestor_direto === 1 ? 'Sim' : 'Não'}</td>
                            <td>
                              {item.gerente_projetos === 1 ? 'Sim' : 'Não'}
                            </td>
                            <td>
                              {item.responsavel_tecnico === 1 ? 'Sim' : 'Não'}
                            </td>
                          </If>
                          <td>
                            <S.Button
                              onClick={() => handleDeleteOverlayOpen(item)}
                            >
                              {actionSvgs('remove')}
                            </S.Button>
                            <S.Button onClick={() => handleItemEdition(item)}>
                              {actionSvgs('edit', '#0083CA')}
                            </S.Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </If>
              </div>
            </S.Table>

            <S.ButtonWrapper>
              <FilledButton
                hasTooltip
                tooltipPosition="top-left"
                tooltipText="Selecione um item na lista"
                text="+ Adicionar campo"
                disabled={!tableData?.length}
                handleClick={handleItemAdd}
              />
            </S.ButtonWrapper>
          </MDBCol>
        </MDBRow>
      </PageStructure>

      <If condition={toastVisibility}>
        {renderToast(
          toastInfos.type,
          toastInfos.message,
          null,
          setToastVisibility,
        )}
      </If>

      <If condition={isOverlayOpen && overlayType !== 'remove'}>
        <ManageListboxValue
          data={overlayData}
          isOpen={isOverlayOpen}
          type={overlayType}
          userID={userID}
          setIsOpen={setIsOverlayOpen}
          setToastVisibility={setToastVisibility}
          setToastInfos={setToastInfos}
          setObserver={setObserver}
        />
      </If>

      <If condition={isOverlayOpen && overlayType === 'remove'}>
        <Overlay
          type="warning"
          isOpen={isOverlayOpen}
          text={[
            'Você está ',
            <span>deletando</span>,
            ' este item. Deseja continuar com esta ação?',
          ]}
        >
          <UnfilledButton
            text="Não"
            handleClick={() => setIsOverlayOpen(false)}
          />
          <FilledButton
            text="Sim, continuar"
            handleClick={handleItemRemove}
            buttonClass="warn"
          />
        </Overlay>
      </If>

      <If condition={loading}>
        <ContentLoader isFull />
      </If>
    </>
  );
}

ManageListboxes.propTypes = {
  user: PropTypes.shape(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  ).isRequired,
};
